import React, { useState } from 'react';
import { Zap, Check, Gift, Sparkles, ArrowRight } from 'lucide-react';
import { motion, Variants, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/auth-context';
import { usePayment } from '@/contexts/PaymentContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PRICING_TIERS } from '@/constants/pricing';

interface PricingSectionProps {
  user: any;
  wishlistedPlans?: Set<string>;
  onWishlist?: (plan: string) => void;
  onScrollToWishlist?: (plan: string) => void;
  onSelectPlan?: (planId: string) => void;
}

export const PricingSection: React.FC<PricingSectionProps> = ({
  user,
  onSelectPlan
}) => {
  const { updateUserPlan } = useAuth();
  const { subscription, isElite } = usePayment();
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = React.useState<'monthly' | 'yearly'>('yearly');
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [targetPlan, setTargetPlan] = useState<string | null>(null);

  const isYearly = billingPeriod === 'yearly';

  const cardContainerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
  };

  const cardItemVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const handleJoinBeta = async () => {
    await updateUserPlan('PRO');
  };

  const handleAction = (planId: string) => {
    if (planId === 'FREE') {
      if (!user) navigate('/register');
      else navigate('/dashboard');
      return;
    }

    setTargetPlan(planId);
    setShowBetaModal(true);
  };

  const confirmWishlist = () => {
    setShowBetaModal(false);
    if (onSelectPlan && targetPlan) {
      onSelectPlan(targetPlan);
    }
    // Scroll to wishlist section
    const wishlistSection = document.getElementById('wishlist-section');
    if (wishlistSection) {
      wishlistSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentPlan = subscription?.plan_id || 'FREE';

  return (
    <section id="pricing" className="py-32 relative overflow-hidden bg-transparent border-t border-black/5">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-br from-[#00e5ff]/5 via-transparent to-[#d500f9]/5 -z-10" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center mb-12 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 border border-cyan-100 mb-6 font-sans text-[10px] uppercase tracking-[0.3em] text-cyan-600 mx-auto">
            <Zap className="w-3.5 h-3.5" />
            <span>Pricing Plans</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter leading-none">Choose Your <span className="text-cyan-600">Path</span></h2>
          <p className="text-zinc-500 text-base font-medium leading-relaxed font-sans mb-12">Whether you play for fun or create for profit, we have a plan for you.</p>

          {/* Pricing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="flex items-center gap-2">
              <span className="bg-emerald-500 text-white text-[8px] font-black px-2 py-1 rounded-full uppercase tracking-tighter flex items-center gap-1">
                20% OFF
              </span>
              <span className={`text-[10px] font-black uppercase tracking-widest ${!isYearly ? 'text-black' : 'text-zinc-400'}`}>Monthly</span>
            </div>
            <button 
              onClick={() => setBillingPeriod(isYearly ? 'monthly' : 'yearly')}
              className="w-14 h-7 bg-cyan-50 rounded-full p-1 transition-all relative border border-cyan-100"
            >
              <motion.div 
                animate={{ x: isYearly ? 28 : 0 }}
                className="w-5 h-5 bg-cyan-600 rounded-full shadow-lg shadow-cyan-600/30"
              />
            </button>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-black uppercase tracking-widest ${isYearly ? 'text-black' : 'text-zinc-400'}`}>Yearly</span>
              <span className="bg-emerald-500 text-white text-[8px] font-black px-2 py-1 rounded-full uppercase tracking-tighter flex items-center gap-1">
                40% OFF <span className="opacity-60 text-[7px] border-l border-white/30 pl-1 ml-1 font-bold">LIMITED</span>
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={cardContainerVariants}
        >
          {PRICING_TIERS.map((plan) => {
            const Icon = plan.icon;
            const isFree = plan.id === 'FREE';
            const isActive = user && currentPlan === plan.id;
            
            const getButtonText = () => {
              if (isActive) return 'Active Plan';
              
              if (plan.id === 'FREE') {
                return user ? 'Active Plan' : 'Explore';
              }
              
              if (plan.id === 'creator_pro') {
                if (isElite) return 'Upgraded';
                return 'Start Trial';
              }
              
              // Keep original logic for others like studio_elite
              return plan.popular && isElite ? 'Upgraded' : `Choose ${plan.name}`;
            };
            
            return (
              <motion.div key={plan.id} variants={cardItemVariants} className={plan.popular ? "scale-105 relative z-10" : ""}>
                <Card className={`${plan.color} ${plan.popular ? 'border-2 border-cyan-500 shadow-[0_40px_80px_-15px_rgba(6,182,212,0.3)]' : 'border border-zinc-950/10 shadow-2xl'} overflow-hidden flex flex-col justify-between rounded-[3rem] transition-all duration-500 hover:scale-[1.02] h-full group relative`}>
                  {plan.popular && (
                    <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden z-20 pointer-events-none">
                      <div 
                        className="absolute top-6 -right-10 px-10 py-2 w-48 text-center text-[10px] font-black text-white uppercase tracking-widest shadow-xl rotate-45 border-y border-white/20 bg-cyan-600"
                        style={{ transformOrigin: 'center' }}
                      >
                        {isYearly ? 'BEST VALUE' : 'POPULAR'}
                      </div>
                    </div>
                  )}

                  <CardHeader className="p-8 pb-4 relative z-10">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center mb-6 shadow-lg"
                      style={{ backgroundColor: plan.accentColor, boxShadow: `0 10px 15px -3px ${plan.accentColor}4D` }}
                    >
                      <Icon className="w-5 h-5 text-black" />
                    </div>
                    <CardTitle className="text-xl font-bold uppercase tracking-tighter text-zinc-950">{plan.name}</CardTitle>
                    <CardDescription className="text-zinc-600 font-medium text-xs mt-2 font-sans">{plan.description}</CardDescription>
                  </CardHeader>

                  <CardContent className="p-8 pt-0 flex-grow relative z-10">
                    <div className="flex flex-col mb-8 font-display">
                      <div className="flex items-baseline gap-3">
                        <div className="text-3xl font-bold tracking-tighter uppercase text-zinc-950">
                          {isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                          {!isFree && <span className="text-[10px] font-bold text-cyan-600 ml-2 font-sans">/ MO</span>}
                        </div>
                        {plan.originalPrice && (
                          <span className="text-zinc-400 text-lg line-through font-medium translate-y-0.5">{plan.originalPrice}</span>
                        )}
                      </div>
                      {!isFree && (
                        <div className="flex flex-col gap-1 mt-1">
                          <span className="text-emerald-600 text-[11px] font-black uppercase tracking-widest">
                            {isYearly ? plan.yearlySubText : plan.monthlySubText}
                          </span>
                          {plan.trialPrice && (
                            <div className="flex items-center gap-2 px-3 py-1 bg-cyan-50 border border-cyan-100 rounded-lg w-fit">
                              <span className="text-cyan-600 text-[9px] font-black uppercase tracking-widest">
                                {plan.trialDuration} Trial for {plan.trialPrice}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {isFree && <div className="text-3xl font-bold tracking-tighter text-zinc-950 uppercase font-display">Free Forever</div>}
                    </div>

                    <ul className="space-y-4">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-center text-[11px] font-semibold text-black font-sans uppercase tracking-widest">
                          <Check className="w-3 h-3 mr-3" style={{ color: plan.accentColor }} /> {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>

                  <CardFooter className="p-8 pt-4 relative z-10 flex flex-col gap-4">
                    <Button 
                      onClick={() => handleAction(plan.id)}
                      variant={plan.popular ? "default" : "outline"}
                      disabled={isActive || (plan.id === 'creator_pro' && isElite)}
                      className={`w-full rounded-xl h-14 uppercase font-bold text-[10px] tracking-widest transition-all font-sans ${
                        plan.popular 
                          ? "bg-cyan-600 text-white hover:bg-cyan-700 shadow-xl shadow-cyan-600/20 border-none" 
                          : "bg-zinc-950/[0.03] text-zinc-950 hover:bg-zinc-950/[0.08] border-zinc-950/10"
                      }`}
                    >
                      {getButtonText()}
                    </Button>
                    
                    {!isFree && (
                      <p className="text-[8px] text-center font-black text-cyan-600/50 uppercase tracking-widest">
                        Waitlist for {plan.name} • Join Beta
                      </p>
                    )}

                    {isFree && user && currentPlan === 'FREE' && (
                      <button 
                        onClick={handleJoinBeta}
                        className="text-[9px] font-bold uppercase tracking-[0.2em] text-zinc-400 hover:text-black transition-colors flex items-center justify-center gap-2 font-sans"
                      >
                        <Gift className="w-3 h-3 text-black" />
                        Join as Beta Tester
                      </button>
                    )}
                  </CardFooter>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      <Dialog open={showBetaModal} onOpenChange={setShowBetaModal}>
        <DialogContent className="sm:max-w-md rounded-[2.5rem] border-none p-10 bg-white/90 backdrop-blur-2xl" showCloseButton={false}>
          <DialogHeader className="text-center space-y-4">
            <div className="w-16 h-16 bg-cyan-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-cyan-100">
               <Sparkles className="w-8 h-8 text-cyan-600" />
            </div>
            <DialogTitle className="text-3xl font-bold font-display uppercase tracking-tight text-zinc-950">We are in Beta</DialogTitle>
            <DialogDescription className="text-zinc-500 font-sans text-sm leading-relaxed px-4">
              Fun Circle Games's Creator Studio is currently in private beta testing. We aren't accepting new payments just yet, but you can join our exclusive wishlist.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-6 space-y-4">
            <div className="bg-zinc-50 p-6 rounded-2xl border border-zinc-100 mb-6">
               <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3 ml-1">Wishlist Perk</h4>
               <p className="text-zinc-950 font-bold font-sans text-sm flex items-center gap-3">
                 <Check className="w-4 h-4 text-emerald-500" />
                 40% Launch Day Discount
               </p>
               <p className="text-zinc-950 font-bold font-sans text-sm flex items-center gap-3 mt-2">
                 <Check className="w-4 h-4 text-emerald-500" />
                 Priority Access Invitation
               </p>
            </div>
            <Button 
              onClick={confirmWishlist}
              className="w-full h-16 rounded-2xl bg-cyan-600 hover:bg-cyan-700 text-white font-bold uppercase tracking-widest text-xs shadow-xl shadow-cyan-600/20"
            >
              Join Wishlist Now <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button 
              variant="link"
              onClick={() => setShowBetaModal(false)}
              className="w-full text-zinc-400 text-[10px] font-bold uppercase tracking-widest hover:text-black transition-colors"
            >
              Maybe later
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PricingSection;
